import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from './client';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface BrandColor {
  id: string;
  brand_id: string;
  role: string;
  hex_value: string;
  label: string | null;
  sort_order: number;
}

export interface BrandFont {
  id: string;
  brand_id: string;
  role: 'heading' | 'body' | 'button';
  family: string;
  source: 'google' | 'adobe' | 'custom' | 'system';
  weights: string;
  fallback: string;
  line_height: string | null;
  letter_spacing: string | null;
  text_transform: string | null;
  custom_url: string | null;
}

export interface BrandAsset {
  id: string;
  brand_id: string;
  label: string;
  asset_type: string;
  url: string;
  r2_key: string | null;
  created_at: string;
}

export interface Brand {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  tagline: string | null;
  brand_voice: string | null;
  tone_keywords: string[] | null;
  guidelines_url: string | null;
  guidelines_summary: string | null;
  logos: {
    primary: string | null;
    dark: string | null;
    icon: string | null;
    hero: string | null;
  };
  default_email: string | null;
  default_phone: string | null;
  default_address: string | null;
  social_links: {
    instagram: string | null;
    facebook: string | null;
    linkedin: string | null;
    youtube: string | null;
  };
  is_default: boolean;
  colors: BrandColor[];
  fonts: BrandFont[];
  assets: BrandAsset[];
  created_at: string;
  updated_at: string;
}

export interface BrandSummary extends Omit<Brand, 'colors' | 'fonts' | 'assets'> {
  color_count: number;
  active_on_tenant: boolean;
}

type RawBrandSummary = Partial<BrandSummary> & {
  logo_primary_url?: string | null;
  logo_dark_url?: string | null;
  logo_icon_url?: string | null;
  hero_image_url?: string | null;
  instagram_url?: string | null;
  facebook_url?: string | null;
  linkedin_url?: string | null;
  youtube_url?: string | null;
  active_on_tenant?: boolean | number | null;
  is_default?: boolean | number | null;
};

function normalizeBrandSummary(raw: RawBrandSummary): BrandSummary {
  return {
    id: raw.id ?? '',
    tenant_id: raw.tenant_id ?? '',
    name: raw.name ?? '',
    slug: raw.slug ?? '',
    tagline: raw.tagline ?? null,
    brand_voice: raw.brand_voice ?? null,
    tone_keywords: raw.tone_keywords ?? null,
    guidelines_url: raw.guidelines_url ?? null,
    guidelines_summary: raw.guidelines_summary ?? null,
    logos: {
      primary: raw.logos?.primary ?? raw.logo_primary_url ?? null,
      dark: raw.logos?.dark ?? raw.logo_dark_url ?? null,
      icon: raw.logos?.icon ?? raw.logo_icon_url ?? null,
      hero: raw.logos?.hero ?? raw.hero_image_url ?? null,
    },
    default_email: raw.default_email ?? null,
    default_phone: raw.default_phone ?? null,
    default_address: raw.default_address ?? null,
    social_links: {
      instagram: raw.social_links?.instagram ?? raw.instagram_url ?? null,
      facebook: raw.social_links?.facebook ?? raw.facebook_url ?? null,
      linkedin: raw.social_links?.linkedin ?? raw.linkedin_url ?? null,
      youtube: raw.social_links?.youtube ?? raw.youtube_url ?? null,
    },
    is_default: Boolean(raw.is_default),
    created_at: raw.created_at ?? '',
    updated_at: raw.updated_at ?? '',
    color_count: raw.color_count ?? 0,
    active_on_tenant: Boolean(raw.active_on_tenant),
  };
}

export interface CreateBrandInput {
  name: string;
  slug?: string;
  tagline?: string;
  brand_voice?: string;
  tone_keywords?: string[];
  guidelines_summary?: string;
  logo_primary_url?: string;
  logo_dark_url?: string;
  logo_icon_url?: string;
  default_email?: string;
  default_phone?: string;
  default_address?: string;
  instagram_url?: string | null;
  facebook_url?: string | null;
  linkedin_url?: string | null;
  youtube_url?: string | null;
  is_default?: boolean;
}

export type UpdateBrandInput = Partial<CreateBrandInput>;

export interface ColorInput {
  role: string;
  hex_value: string;
  label?: string;
  sort_order?: number;
}

export interface FontInput {
  role: 'heading' | 'body' | 'button';
  family: string;
  source?: 'google' | 'adobe' | 'custom' | 'system';
  weights?: string;
  fallback?: string;
  line_height?: string;
  letter_spacing?: string;
  text_transform?: string;
  custom_url?: string;
}

// ── API Functions ─────────────────────────────────────────────────────────────

export async function listBrands(): Promise<BrandSummary[]> {
  const res = await apiGet<{ brands: RawBrandSummary[] }>('/brands');
  return (res.brands ?? []).map(normalizeBrandSummary);
}

export async function getBrand(id: string): Promise<Brand> {
  const res = await apiGet<{ brand: Brand }>(`/brands/${id}`);
  return res.brand;
}

export async function createBrand(input: CreateBrandInput): Promise<Brand> {
  const res = await apiPost<{ brand: Brand }>('/brands', input);
  return res.brand;
}

export async function updateBrand(id: string, input: UpdateBrandInput): Promise<Brand> {
  const res = await apiPut<{ brand: Brand }>(`/brands/${id}`, input);
  return res.brand;
}

export async function deleteBrand(id: string): Promise<void> {
  await apiDelete(`/brands/${id}`);
}

export async function updateBrandColors(id: string, colors: ColorInput[]): Promise<BrandColor[]> {
  const res = await apiPut<{ colors: BrandColor[] }>(`/brands/${id}/colors`, { colors });
  return res.colors;
}

export async function updateBrandFonts(id: string, fonts: FontInput[]): Promise<BrandFont[]> {
  const res = await apiPut<{ fonts: BrandFont[] }>(`/brands/${id}/fonts`, { fonts });
  return res.fonts;
}

export async function addBrandAsset(
  id: string,
  asset: { label: string; asset_type?: string; url: string; r2_key?: string },
): Promise<BrandAsset> {
  const res = await apiPost<{ asset: BrandAsset }>(`/brands/${id}/assets`, asset);
  return res.asset;
}

/** Update an asset in place (e.g. replace its url after an AI edit, or rename it). */
export async function updateBrandAsset(
  brandId: string,
  assetId: string,
  patch: { label?: string; url?: string; r2_key?: string | null },
): Promise<BrandAsset> {
  const res = await apiPut<{ asset: BrandAsset }>(`/brands/${brandId}/assets/${assetId}`, patch);
  return res.asset;
}

export async function deleteBrandAsset(brandId: string, assetId: string): Promise<void> {
  await apiDelete(`/brands/${brandId}/assets/${assetId}`);
}

export async function activateBrand(id: string): Promise<void> {
  await apiPut(`/brands/${id}/activate`, {});
}

export async function deactivateBrand(): Promise<void> {
  await apiDelete('/brands/active');
}

// ── File upload types ─────────────────────────────────────────────────────────

export type LogoVariant = 'primary' | 'dark' | 'white' | 'icon' | 'hero';

export interface ExtractedColor { name: string; hex: string; usage: string; }
export interface ExtractedFont  { name: string; usage: string; weights: string; }

export interface ExtractedGuidelines {
  colors:           ExtractedColor[];
  fonts:            ExtractedFont[];
  voiceAndTone:     string;
  logoRules:        string;
  colorUsage:       string;
  photographyStyle: string;
  summary:          string;
}

export interface CustomFontFile {
  weight:  number;
  style:   'normal' | 'italic';
  url:     string;
  r2_key:  string;
  format:  string;
}

// ── Upload API functions ──────────────────────────────────────────────────────

export async function uploadLogo(
  brandId: string,
  variant: LogoVariant,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ url: string; variant: string; r2_key: string }> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('variant', variant);
  return apiUpload(`/brands/${brandId}/logos`, fd, onProgress);
}

export async function deleteLogo(brandId: string, variant: LogoVariant): Promise<void> {
  await apiDelete(`/brands/${brandId}/logos/${variant}`);
}

export async function uploadGuidelines(
  brandId: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<{ file_url: string; extracted: ExtractedGuidelines | null }> {
  const fd = new FormData();
  fd.append('file', file);
  return apiUpload(`/brands/${brandId}/guidelines`, fd, onProgress);
}

export async function uploadFont(
  brandId: string,
  opts: {
    file:        File;
    fontFamily:  string;
    weight:      number;
    style:       'normal' | 'italic';
    role:        'heading' | 'body' | 'button';
  },
  onProgress?: (pct: number) => void,
): Promise<{ url: string; r2_key: string; font_face: string }> {
  const fd = new FormData();
  fd.append('file',        opts.file);
  fd.append('font_family', opts.fontFamily);
  fd.append('weight',      String(opts.weight));
  fd.append('style',       opts.style);
  fd.append('role',        opts.role);
  return apiUpload(`/brands/${brandId}/fonts/upload`, fd, onProgress);
}

// ── Curated Google Fonts list ─────────────────────────────────────────────────

export const GOOGLE_FONTS: string[] = [
  // Sans-serif
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Nunito', 'Poppins',
  'Source Sans 3', 'Raleway', 'Oswald', 'Ubuntu', 'Noto Sans', 'Mulish',
  'Work Sans', 'Fira Sans', 'DM Sans', 'Plus Jakarta Sans', 'Outfit',
  'Sora', 'Lexend', 'Jost', 'Figtree', 'Karla',
  // Serif
  'Merriweather', 'Playfair Display', 'Lora', 'EB Garamond', 'Cormorant Garamond',
  'DM Serif Display', 'Libre Baskerville', 'Noto Serif', 'PT Serif',
  'Source Serif 4', 'Spectral', 'Bitter',
  // Display / Decorative
  'Space Grotesk', 'Bebas Neue', 'Anton', 'Abril Fatface', 'Righteous',
  'Lobster', 'Pacifico', 'Dancing Script', 'Great Vibes',
  // Monospace
  'JetBrains Mono', 'Fira Code', 'Source Code Pro', 'Space Mono', 'Roboto Mono',
].sort();

export const DEFAULT_COLOR_ROLES: { role: string; label: string; defaultHex: string }[] = [
  { role: 'primary',              label: 'Primary',               defaultHex: '#1a1a2e' },
  { role: 'secondary',            label: 'Secondary',             defaultHex: '#16213e' },
  // The explicit answer to "which color controls the footer" — asked from real use. Defaults to
  // the brand's Secondary in the editor (the footer's historical source), so adding the swatch
  // changes nothing until someone deliberately picks a different footer color.
  { role: 'footer',               label: 'Footer background',     defaultHex: '#16213e' },
  { role: 'accent',               label: 'Accent',                defaultHex: '#0f3460' },
  { role: 'neutral_dark',         label: 'Neutral Dark',          defaultHex: '#1a1a1a' },
  { role: 'neutral_light',        label: 'Neutral Light',         defaultHex: '#f5f5f5' },
  { role: 'text_primary',         label: 'Text Primary',          defaultHex: '#111111' },
  { role: 'text_secondary',       label: 'Text Secondary',        defaultHex: '#555555' },
  { role: 'button_primary_bg',    label: 'Button Primary BG',     defaultHex: '#1a1a2e' },
  { role: 'button_primary_text',  label: 'Button Primary Text',   defaultHex: '#ffffff' },
  { role: 'button_primary_hover', label: 'Button Primary Hover',  defaultHex: '#16213e' },
  { role: 'link_color',           label: 'Link Color',            defaultHex: '#0f3460' },
  { role: 'link_hover',           label: 'Link Hover',            defaultHex: '#1a1a2e' },
];
